import React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { requireCoachAuth } from "~/lib/coach-middleware";
import {
  createResource,
  getResourceById,
  updateResource,
} from "~/lib/server/coach";
import { useCoachPrivateProfileRequired } from "~/contexts/coach";
import {
  FormFileInputField,
  FormInputField,
} from "~/components/form/form-fields";
import { FormField } from "~/components/ui/form-field";
import { Textarea } from "~/components/ui/textarea";
import { ProfileEditHeader } from "~/components/profile-edit-header";
import {
  UnsavedChangesAlert,
  useUnsavedChangesAlert,
} from "~/components/unsaved-changes-alert";
import { toast } from "sonner";

const ResourceSchema = z.object({
  id: z.string().optional(),
  coachId: z.string(),
  title: z
    .string()
    .min(3, "Title must be at least 3 characters")
    .max(100, "Title must be less than 100 characters"),
  description: z
    .string()
    .min(20, "Description must be at least 20 characters")
    .max(200, "Description must be less than 200 characters"),
  content: z.string().min(20, "Content must be at least 20 characters"),
  imageSrc: z.string().url("Must be a valid URL").optional().nullable(),
  externalLinkText: z.string().optional().nullable(),
  externalLinkUrl: z.string().url("Must be a valid URL").optional().nullable(),
});

type FormData = z.infer<typeof ResourceSchema>;

export const getResourceData = createServerFn({ method: "GET" })
  .middleware([requireCoachAuth])
  .validator((data: unknown) =>
    z.object({ resourceId: z.string() }).parse(data),
  )
  .handler(async ({ data, context }) => {
    const { resourceId } = data;
    const coach = context.coach;

    if ("new" === resourceId) {
      return {
        id: undefined,
        coachId: coach.id,
        title: "",
        description: "",
        content: "",
        imageSrc: null,
        externalLinkText: null,
        externalLinkUrl: null,
      };
    }

    const resource = await getResourceById(resourceId);

    if (!resource || resource.coachId !== coach.id) {
      throw new Error("Resource not found or access denied");
    }

    return resource;
  });

export const saveResource = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .validator((data: unknown) => ResourceSchema.parse(data))
  .handler(async ({ data, context }) => {
    const coach = context.coach;
    const { id, coachId, ...resourceData } = data;

    // Ensure the resource belongs to the authenticated coach
    if (coachId !== coach.id) {
      throw new Error("Access denied");
    }

    if (id) {
      // Update existing resource
      const existingResource = await getResourceById(id);
      if (!existingResource || existingResource.coachId !== coach.id) {
        throw new Error("Resource not found or access denied");
      }
      await updateResource(id, resourceData);
    } else {
      // Create new resource
      await createResource({
        coachId: coach.id,
        ...resourceData,
      });
    }

    return { status: "success", message: "Resource saved successfully" };
  });

export const Route = createFileRoute(
  "/coach/_backend/edit-profile/resources/$resourceId/",
)({
  head: ({ params }) => ({
    meta: [
      {
        title:
          "new" === params.resourceId
            ? "New Resource – Dream Coach Match"
            : "Edit Resource – Dream Coach Match",
      },
    ],
  }),
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData({
      queryKey: ["resource", params.resourceId],
      queryFn: () =>
        getResourceData({ data: { resourceId: params.resourceId } }),
    });

    return {};
  },
  component: ResourceEditPage,
});

function ResourceEditPage() {
  const { resourceId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const coach = useCoachPrivateProfileRequired();

  const { data: resourceData, isLoading } = useQuery({
    queryKey: ["resource", resourceId],
    queryFn: () => getResourceData({ data: { resourceId } }),
  });

  const { register, handleSubmit, formState, reset, control } =
    useForm<FormData>({
      resolver: zodResolver(ResourceSchema),
      defaultValues: resourceData,
    });

  // Reset form when data loads
  React.useEffect(() => {
    if (resourceData) {
      reset(resourceData);
    }
  }, [resourceData, reset]);

  const { errors, isDirty } = formState;

  const saveMutation = useMutation({
    mutationFn: (data: FormData) => saveResource({ data }),
    onSuccess: (_, variables) => {
      toast.success("Resource saved successfully");
      queryClient.invalidateQueries({ queryKey: ["coach", "resources"] });
      queryClient.invalidateQueries({ queryKey: ["resource", resourceId] });
      reset(variables); // Reset form to clear dirty state
      navigate({ to: "/coach/edit-profile/resources" });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to save resource");
    },
  });

  const onSubmit = (data: FormData) => {
    saveMutation.mutate(data);
  };

  const { showExitAlert, onDiscard } = useUnsavedChangesAlert(() => isDirty);

  if (isLoading) {
    return <div>Loading resource...</div>;
  }

  const isNew = "new" === resourceId;

  return (
    <>
      <UnsavedChangesAlert show={showExitAlert} onDiscard={onDiscard} />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <ProfileEditHeader
          title={isNew ? "New Resource" : "Edit Resource"}
          form={formState}
          username={coach.username}
        />

        {/* Hidden fields */}
        <input type="hidden" {...register("coachId")} />
        {!isNew && <input type="hidden" {...register("id")} />}

        <Card>
          <CardHeader>
            <CardTitle>Resource Details</CardTitle>
            <CardDescription>
              Create valuable resources for your audience to download and use.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormInputField
              control={control}
              name="title"
              label="Title"
              required
              helpText="A clear, descriptive title for your resource"
            />

            <FormField
              label="Description"
              required
              helpText="A brief description that will be shown in the resource list (20-200 characters)"
              error={errors.description?.message}
            >
              <Textarea {...register("description")} maxLength={200} rows={3} />
            </FormField>

            <FormField
              label="Content"
              required
              helpText="The main content of your resource. This will be displayed on the resource page."
              error={errors.content?.message}
            >
              <Textarea {...register("content")} rows={8} />
            </FormField>

            <FormFileInputField
              control={control}
              name="imageSrc"
              label="Resource Image"
              helpText="An optional image to represent your resource"
              recommendedDimensions={{ width: 1200, height: 630 }}
              accept=".jpg,.jpeg,.png,.webp"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Downloadable File</CardTitle>
            <CardDescription>
              Add a downloadable file that your audience can access. This could
              be a PDF guide, worksheet, template, or any other resource that
              adds value to your content.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormFileInputField
              control={control}
              name="externalLinkUrl"
              label="Downloadable File"
              helpText="Upload your file (PDF, DOCX, XLSX, etc.) - Maximum 10MB"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.zip,.mp3,.wav,.m4a,.aac"
              maxSize={10 * 1024 * 1024} // 10MB limit
            />

            <FormInputField
              control={control}
              name="externalLinkText"
              label="File Display Name"
              helpText="The name that will be shown to your audience (e.g., 'Social Media Strategy Template')"
            />
          </CardContent>
        </Card>
      </form>
    </>
  );
}
